import clsx from "clsx";
import Image from "next/image";
import { useRouter } from "next/router";
import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { findFeaturedPortraitImage } from "@chef/state-management/helpers";
import { MealCard, Tag } from "@chef/components";
import {
  PickAndMixQuery,
  selectDeviationProducts,
  setRecipeModal,
} from "@chef/state-management";
import {
  BASE_64_1X1_GREY,
  BRAND_PATHS,
  config,
  FILTER_CATEGORIES,
  language,
  TAXONOMY_IDS,
} from "@chef/constants";
import {
  useFavorites,
  useUpdateBasketPickAndMixDeviation,
} from "@chef/state-management/hooks";
import { isPickAndMixProduct, slugify } from "@chef/helpers";
import {
  generateTags,
  shouldShowRecipeByFilter,
} from "@chef/state-management/helpers";
import { useCallbackRef } from "@chef/hooks";
import {
  ImageTags,
  NoFavoritesThisWeek,
  RecipeReactionButton,
  RecipeTags,
} from "@chef/smart-components";
import { isEmptyArray, isNotEmptyArray } from "@chef/utils/array";
import { nonNullable } from "@chef/utils/nonNullable";

import {
  usePickAndMixProductWithPrice,
  useScrollIntoViewHighlight,
} from "../hooks";

type UpdateBasketDeviationArgs = {
  product: PickAndMixQuery["pickAndMix"][number]["product"];
  variationId: string;
};

interface MealCardContainerProps {
  pickAndMixProduct: PickAndMixQuery["pickAndMix"][number];
  recipe: PickAndMixQuery["pickAndMix"][number]["recipes"][number];
  price: number;
  url: string;
  variationId: string;
  updateBasketDeviation: (args: UpdateBasketDeviationArgs) => void;
  onShowBasket: (e: React.MouseEvent<HTMLButtonElement>) => void;
  hideFooter: boolean;
  disabled: boolean;
  inBasket?: boolean;
  hasPlusProtein: boolean;
  plusVariationIsInBasket?: boolean;
  signup: boolean;
  showAverageRating: boolean;
  priority: boolean;
}
// eslint-disable-next-line react/display-name
const MealCardContainer = React.memo(
  ({
    pickAndMixProduct,
    recipe,
    price,
    url,
    variationId,
    updateBasketDeviation,
    onShowBasket,
    hideFooter,
    disabled,
    inBasket,
    hasPlusProtein,
    plusVariationIsInBasket,
    signup,
    showAverageRating,
    priority,
  }: MealCardContainerProps) => {
    const dispatch = useDispatch();

    const { product } = pickAndMixProduct;
    const { campaign } = generateTags(recipe);
    const { recipeId, recipeName } = recipe || {};

    const modalRoute = BRAND_PATHS.RECIPE_WITH_NAME_HREF({
      recipeId,
      recipeName: slugify(recipeName),
    });

    const isWeightWatchers =
      TAXONOMY_IDS.WEIGHT_WATCHERS &&
      campaign.id === TAXONOMY_IDS.WEIGHT_WATCHERS.toString();

    return (
      <MealCard
        data-recipe-id={recipe.mainRecipeId ?? recipe.recipeId}
        key={recipe.recipeId}
        className="h-full"
        language={language}
        text={recipe.recipeName}
        price={price}
        tags={<RecipeTags recipe={recipe} />}
        href={modalRoute}
        id={recipe.recipeId.toString()}
        numberOfRatings={!isWeightWatchers ? recipe.numberOfRatings : undefined}
        averageRating={!isWeightWatchers ? recipe.averageRating : undefined}
        CampaignTag={({ className }) => (
          <div className={clsx(className, !campaign?.text && "hidden")}>
            <Tag highlight className="normal-case !px-1">
              <p className="truncate text-ellipsis max-w-[25ch] text-base px-2">
                {campaign?.text}
              </p>
            </Tag>
          </div>
        )}
        Image={() => (
          <Image
            src={url || BASE_64_1X1_GREY}
            width={2}
            height={3}
            className="object-cover w-full h-auto rounded-t aspect-2/3"
            alt={recipe.recipeName}
            sizes="(max-width: 375px) 35vw, (max-width: 768px) 25vw, 20vw"
            priority={priority}
          />
        )}
        onAddRemoveProduct={() =>
          updateBasketDeviation({
            product,
            variationId,
          })
        }
        onShowBasket={onShowBasket}
        hideFooter={hideFooter}
        disabled={disabled}
        inBasket={inBasket}
        ImageTag={
          <ImageTags
            price={price}
            hasPlusProtein={hasPlusProtein}
            inBasket={plusVariationIsInBasket}
          />
        }
        onClick={(e) => {
          e.preventDefault();
          dispatch(
            setRecipeModal({
              open: true,
              recipeId: recipe.recipeId,
              pickAndMixProduct,
              misc: {
                variationId,
              },
              view: signup ? "conversion" : "deviation",
            }),
          );
        }}
        showAverageRating={showAverageRating}
      />
    );
  },
);

interface PickAndMixCardGridProps {
  items: PickAndMixQuery["pickAndMix"];
  filter?: {
    description?: string;
    label?: string;
    value: string;
  };
  recommendations?: Set<string>;
  portions: number;
  week: number;
  year: number;
  hideFooter?: boolean;
  className?: string;
  onModalAction?: () => void;
  signup?: boolean;
  resetFilters?: () => void;
}

export const PickAndMixCardGrid = ({
  items: pickAndMixItems,
  filter: pickAndMixFilter,
  recommendations = new Set(),
  portions,
  week,
  year,
  hideFooter = false,
  className,
  onModalAction = () => null,
  signup = false,
  resetFilters,
}: PickAndMixCardGridProps) => {
  const router = useRouter();
  const showAverageRating = !signup;
  const scrollToRecipeId = router.query["recipeId"];

  const { pickAndMix } = usePickAndMixProductWithPrice({
    week,
    year,
    sortedPickAndMix: pickAndMixItems,
    signup,
  });

  const { favorites, enableFavorites } = useFavorites({
    sticky: pickAndMixFilter?.value === FILTER_CATEGORIES.FAVORITES_SLUG,
  });

  const { updateBasketPickAndMixDeviation } =
    useUpdateBasketPickAndMixDeviation({ week, year });

  const selectedProducts = useSelector(selectDeviationProducts({ week, year }));

  const pickAndMixProductsInBasket =
    selectedProducts?.filter(isPickAndMixProduct) || [];

  const updateBasketDeviation = useCallbackRef(
    ({ product, variationId }: UpdateBasketDeviationArgs) => {
      updateBasketPickAndMixDeviation({
        basketProducts: selectedProducts || [],
        portions,
        updateProduct: {
          productId: product.productId,
          variationId,
        },
      });
    },
    [week, year],
  );

  const handleShowBasket = useCallbackRef(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      onModalAction && onModalAction();
    },
  );

  const recipes = pickAndMix
    .flatMap((pmx) => {
      const recipe = pmx.recipes[0];

      const shouldShow = shouldShowRecipeByFilter({
        recipe,
        filter:
          pickAndMixFilter?.value || FILTER_CATEGORIES.ALL_CATEGORIES_SLUG,
        recommendations,
        favorites,
        productId: pmx.productId,
        price: recipe.price,
      });

      if (!shouldShow) {
        return null;
      }

      return {
        pickAndMixProduct: pmx,
        recipe,
      };
    })
    .filter(nonNullable);

  const hasFilterDescription =
    pickAndMixFilter?.description && pickAndMixFilter?.label;

  const noRecipesVisible = isEmptyArray(recipes);

  const showNoFavorites =
    pickAndMixFilter?.value === FILTER_CATEGORIES.FAVORITES_SLUG &&
    resetFilters &&
    enableFavorites &&
    noRecipesVisible;

  useScrollIntoViewHighlight(
    typeof scrollToRecipeId === "string" ? scrollToRecipeId : undefined,
    {
      dataAttribute: "data-recipe-id",
      duration: 5000,
      shouldScroll: isNotEmptyArray(recipes),
    },
  );

  return (
    <div className={className}>
      {hasFilterDescription && (
        <div className="flex justify-center">
          <div className="w-full px-6 py-4 text-center bg-informationBG md:w-144">
            <span className="text-sm">
              <strong>{pickAndMixFilter.label}</strong>
              <p>{pickAndMixFilter.description}</p>
            </span>
          </div>
        </div>
      )}

      <div
        className="grid grid-cols-2 gap-2 mt-4 lg:gap-4 md:grid-cols-3 xl:grid-cols-5"
        id="pick-and-mix-card-grid"
      >
        {showNoFavorites && (
          <NoFavoritesThisWeek key="no-favorites" onReset={resetFilters} />
        )}
        {recipes.map(({ pickAndMixProduct, recipe }, index) => {
          const {
            price,
            variation,
            productIsInBasket,
            hasPlusProtein,
            plusVariationIsInBasket,
          } = recipe;

          const url = findFeaturedPortraitImage(recipe)?.url;

          const key = `${recipe.recipeId}-${recipe.variation.variationId}`;

          return (
            <div key={key} className="relative">
              <MealCardContainer
                priority={index < 5}
                pickAndMixProduct={pickAndMixProduct}
                recipe={recipe}
                price={price}
                url={url || BASE_64_1X1_GREY}
                variationId={variation?.variationId}
                updateBasketDeviation={updateBasketDeviation}
                onShowBasket={handleShowBasket}
                hideFooter={hideFooter}
                disabled={
                  pickAndMixProductsInBasket.length === config.order.maxMeals &&
                  !productIsInBasket
                }
                inBasket={productIsInBasket}
                hasPlusProtein={hasPlusProtein}
                plusVariationIsInBasket={plusVariationIsInBasket}
                signup={signup}
                showAverageRating={showAverageRating}
              />
              <RecipeReactionButton
                recipe={recipe}
                className="absolute z-10 shadow md:top-3 md:right-3 top-2 right-2"
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
